import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, HeartPulse } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { usePulseCheckInNudge } from "@/hooks/use-pulse-check-in-nudge";
import { formatPulsePeriodLabel } from "@/lib/pulse-period-label";
import { cn } from "@/lib/utils";
import { PULSE_OVERDUE_DAYS, daysSincePeriodEnd } from "@/lib/pulse-banner";

const PULSE_PATH = "/coach/performance/pulse";

function snoozeKey(businessId: string, periodStart: string): string {
  return `pulse-banner-snooze:${businessId}:${periodStart}`;
}

/** Tomorrow 07:00 local: "remind me tomorrow" means the next working morning. */
function tomorrowMorningMs(now: Date = new Date()): number {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(7, 0, 0, 0);
  return d.getTime();
}

function readSnoozedUntil(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    const ms = raw == null ? NaN : Number(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

/**
 * "Dags för Pulse check-in" on every page, not only /coach (GRA-60). Shown to
 * whoever can submit Pulse once their own check-in period has ended and not
 * everyone is rated — the same rule as the Today nudge. It can be put off
 * until tomorrow morning but not closed for the period: the check-in is the
 * team leader's job. It turns red once the check-in is
 * `PULSE_OVERDUE_DAYS` days late, and disappears by itself when done.
 */
export function PulseCheckInBanner() {
  const { t } = useTranslation("common");
  const { businessProfile, memberRole } = useProfile();
  const { pathname } = useLocation();
  const nudge = usePulseCheckInNudge(businessProfile, memberRole);
  const businessId = businessProfile?.uid ?? "";
  const summary = nudge.summary;
  const periodStart = summary?.checkInPeriod.periodStart ?? "";
  const key = snoozeKey(businessId, periodStart);

  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  useEffect(() => {
    setSnoozedUntil(periodStart ? readSnoozedUntil(key) : null);
  }, [key, periodStart]);

  if (!nudge.show || summary == null) return null;
  // Redundant on the page where the check-in is done, and on the home page,
  // whose deadline column already carries the check-in.
  if (pathname.startsWith(PULSE_PATH) || pathname === "/") return null;
  if (snoozedUntil != null && Date.now() < snoozedUntil) return null;

  const { periodEnd } = summary.checkInPeriod;
  const overdue = daysSincePeriodEnd(periodEnd) >= PULSE_OVERDUE_DAYS;
  const label = formatPulsePeriodLabel(periodStart, periodEnd);
  const params = new URLSearchParams({ periodStart, periodEnd });

  const snooze = () => {
    const until = tomorrowMorningMs();
    try {
      window.localStorage.setItem(key, String(until));
    } catch {
      // Private mode or blocked storage: hide it for this session only.
    }
    setSnoozedUntil(until);
  };

  const Icon = overdue ? AlertTriangle : HeartPulse;
  return (
    // Same insets as the content column below it, so it reads as part of
    // the page rather than a strip across the shell. Status tints from the
    // tokens: amber while due, red once overdue.
    <div className="px-4 pt-3 font-g sm:px-8 sm:pt-4">
      <div
        role="status"
        className={cn(
          "rounded-g-lg border-[1.5px] text-g-ink-900",
          overdue
            ? "border-g-danger-fill bg-g-danger-tint"
            : "border-g-warning-fill bg-g-warning-tint",
        )}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5">
          <Icon className={cn("h-4 w-4 shrink-0", overdue ? "text-g-danger" : "text-g-warning")} />
          <span className="min-w-0 flex-1 text-sm leading-5">
            <span className="font-semibold">
              {overdue ? t("pulseBanner.titleOverdue") : t("pulseBanner.title")}
            </span>{" "}
            {summary.scoredCount > 0
              ? t("pulseBanner.progress", {
                  done: summary.scoredCount,
                  total: summary.totalMembers,
                  period: label,
                })
              : t("pulseBanner.notStarted", { period: label })}
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={snooze}
              className="text-xs font-semibold text-g-ink-600 underline underline-offset-2 hover:text-g-ink-900"
            >
              {t("pulseBanner.later")}
            </button>
            <Link
              to={`${PULSE_PATH}?${params.toString()}`}
              className={cn(
                "rounded-g-xs px-3 py-1.5 text-[13px] font-bold text-g-on-primary shadow-g-button",
                overdue ? "bg-g-danger-fill hover:bg-g-danger" : "bg-g-primary hover:bg-g-primary-hover",
              )}
            >
              {t("pulseBanner.start")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
